import React, { useEffect, useMemo, useState } from "react";
import { Card, Select, Input, Spinner } from "@/components/UI";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { CheckCircle2, FolderKanban, Zap, Search, ListTodo, Calendar, Sparkles, ChevronDown, ChevronRight } from "lucide-react";
import TaskModal from "@/components/TaskModal";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";

const tooltipStyle = { background: "#0f172a", border: "1px solid #334155", borderRadius: 8, fontSize: 12 };
const MONTHS = [
  { value: "", label: "Semua Bulan" },
  { value: "1", label: "Jan" },
  { value: "2", label: "Feb" },
  { value: "3", label: "Mar" },
  { value: "4", label: "Apr" },
  { value: "5", label: "Mei" },
  { value: "6", label: "Jun" },
  { value: "7", label: "Jul" },
  { value: "8", label: "Agu" },
  { value: "9", label: "Sep" },
  { value: "10", label: "Okt" },
  { value: "11", label: "Nov" },
  { value: "12", label: "Des" },
];

function buildYearOptions() {
  const now = new Date().getFullYear();
  const list = [];
  for (let y = now - 2; y <= now + 2; y++) list.push({ value: String(y), label: String(y) });
  return list;
}

export default function EmployeeLaporan({ userId } = {}) {
  const { user: me } = useAuth();
  const [viewUser, setViewUser] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [yearSprintPerf, setYearSprintPerf] = useState(null);
  const [sprintPerf, setSprintPerf] = useState(null);
  const [extraData, setExtraData] = useState({ total_extra: 0, items: [] });
  const [search, setSearch] = useState("");
  const [expandedKey, setExpandedKey] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [month, setMonth] = useState(String(new Date().getMonth() + 1));

  useEffect(() => {
    if (!me) return;
    if (!userId || String(userId) === String(me.id)) {
      setViewUser(me);
      return;
    }
    api.get("/users")
      .then((r) => setViewUser(r.data.find((u) => String(u.id) === String(userId)) || me))
      .catch(() => setViewUser(me));
  }, [userId, me]);

  useEffect(() => {
    if (!viewUser) return;
    setLoading(true);
    Promise.all([
      api.get("/tasks", { params: { assignee_id: viewUser.id } }).then((r) => r.data || []),
      api.get("/projects").then((r) => r.data || []),
    ])
      .then(([t, p]) => {
        setTasks(t);
        setProjects(p);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [viewUser]);

  useEffect(() => {
    if (!viewUser) return;
    setSprintPerf(null);
    Promise.all([
      api.get("/dashboard/performance", { params: { user_id: viewUser.id, type: "sprint", year } }).then((r) => r.data),
      api.get("/dashboard/extra-tasks", { params: { user_id: viewUser.id, year } }).then((r) => r.data || { total_extra: 0, items: [] }),
    ])
      .then(([perfData, extra]) => {
        setYearSprintPerf(perfData);
        setExtraData(extra);
      })
      .catch(() => {});
  }, [viewUser, year]);

  useEffect(() => {
    if (!viewUser || !month) {
      setSprintPerf(null);
      return;
    }
    api.get("/dashboard/performance", { params: { user_id: viewUser.id, type: "sprint", year, month } })
      .then((r) => setSprintPerf(r.data))
      .catch(() => {});
  }, [viewUser, year, month]);

  // View per tahun = rollup sprint per bulan mulainya (sama basisnya dengan view per bulan)
  const yearlySeries = useMemo(() => {
    const buckets = MONTHS.slice(1).map((m, i) => ({ month: i + 1, label: m.label, output: 0, allocated: 0, performance: null }));
    (yearSprintPerf?.series || []).forEach((r) => {
      const b = buckets[(r.month || 0) - 1];
      if (!b) return;
      b.output += r.output || 0;
      b.allocated += r.allocated || 0;
    });
    buckets.forEach((b) => {
      b.performance = b.allocated > 0 ? Math.min(100, Math.round((b.output / b.allocated) * 1000) / 10) : null;
    });
    return buckets;
  }, [yearSprintPerf]);

  const series = useMemo(() => {
    if (month && sprintPerf?.series) return sprintPerf.series;
    return yearlySeries;
  }, [month, sprintPerf, yearlySeries]);

  const filteredOutput = series.reduce((s, p) => s + (p.output || 0), 0);
  const filteredAllocated = series.reduce((s, p) => s + (p.allocated || 0), 0);
  const overallPerformance = filteredAllocated > 0 ? Math.min(100, Math.round((filteredOutput / filteredAllocated) * 100)) : null;

  const yearNum = Number(year);
  const yearTasks = tasks.filter((t) => {
    const d = t.deadline || t.start_date || t.created_at;
    return d && new Date(d).getFullYear() === yearNum && !["backlog", "planned", "cancelled", "replaced"].includes(t.status);
  });
  const yearTotal = yearTasks.reduce((s, t) => s + (t.weight || 0), 0);
  const yearDone = yearTasks.filter((t) => t.status === "done").reduce((s, t) => s + (t.weight || 0), 0);
  const yearRemaining = Math.max(yearTotal - yearDone, 0);
  const todayStr = new Date().toLocaleDateString("sv-SE");
  const yearOverdue = yearTasks.filter((t) => t.status !== "done" && t.deadline && t.deadline.slice(0, 10) < todayStr).length;

  const completedProjectIds = useMemo(() => new Set(tasks.filter((t) => t.status === "done" && t.project_id).map((t) => t.project_id)), [tasks]);
  const completedProjects = projects.filter((p) => p.status === "completed" && completedProjectIds.has(p.id));
  const completedTasks = useMemo(() => tasks.filter((t) => t.status === "done"), [tasks]);

  // Group completed tasks + extra tasks by project (or "Internal" when no project).
  const groups = useMemo(() => {
    const map = new Map();
    completedTasks.forEach((t) => {
      const key = t.project_id ? String(t.project_id) : "internal";
      if (!map.has(key)) map.set(key, { key, project: t.project || null, done: [], extra: [] });
      map.get(key).done.push(t);
    });
    (extraData.items || []).forEach((item) => {
      const key = item.project?.id ? String(item.project.id) : "internal";
      if (!map.has(key)) map.set(key, { key, project: item.project || null, done: [], extra: [] });
      map.get(key).extra.push(item);
    });
    return Array.from(map.values()).sort((a, b) => {
      if (a.key === "internal") return 1;
      if (b.key === "internal") return -1;
      return (a.project?.name || "").localeCompare(b.project?.name || "");
    });
  }, [completedTasks, extraData]);

  // Global search: flatten across all projects when search is active.
  const searchActive = search.trim().length > 0;
  const flatSearchResults = useMemo(() => {
    if (!searchActive) return [];
    const q = search.trim().toLowerCase();
    const doneMatches = completedTasks
      .filter((t) => t.title.toLowerCase().includes(q))
      .map((t) => ({ ...t, _kind: "done" }));
    const extraMatches = (extraData.items || [])
      .filter((it) => it.title.toLowerCase().includes(q))
      .map((it) => ({ ...it, _kind: "extra" }));
    return [...doneMatches, ...extraMatches];
  }, [search, searchActive, completedTasks, extraData]);

  const findFullTask = (item) => tasks.find((tt) => tt.id === item.id) || item;

  if (loading) return <Spinner />;

  return (
    <div className="mx-auto max-w-6xl space-y-6" data-testid="employee-laporan-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">
          Laporan {viewUser && me?.id !== viewUser?.id ? viewUser?.name : "Saya"}
        </h1>
        <p className="text-sm text-slate-400">Ringkasan tahun, project selesai, dan task selesai.</p>
      </div>

      {/* Ringkasan Tahun */}
      <Card className="p-5" data-testid="laporan-yearly-summary">
        <h2 className="mb-4 font-heading text-lg font-semibold">Ringkasan Tahun {year}</h2>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Metric icon={Zap} color="#D1D5DB" label="Total Bobot" value={yearTotal} unit="pts" />
          <Metric icon={CheckCircle2} color="#22C55E" label="Bobot Selesai" value={yearDone} unit="pts" />
          <Metric icon={ListTodo} color="#9CA3AF" label="Tersisa" value={yearRemaining} unit="pts" />
          <Metric icon={Zap} color="#F43F5E" label="Overdue" value={yearOverdue} unit="task" />
        </div>
        <div className="mt-5">
          <div className="mb-1.5 flex justify-between text-xs text-slate-400">
            <span>Progress tahun ini</span>
            <span className="font-mono text-slate-400">{yearDone}/{yearTotal} pts</span>
          </div>
          <div className="h-2 w-full rounded-full bg-slate-800">
            <div
              className="h-2 rounded-full bg-slate-400"
              style={{ width: `${yearTotal > 0 ? (yearDone / yearTotal) * 100 : 0}%` }}
            />
          </div>
        </div>
      </Card>

      {/* Ringkasan Project & Task */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card className="p-5" data-testid="laporan-completed-projects">
          <div className="mb-1 flex items-center gap-2 text-xs text-slate-400">
            <FolderKanban size={14} /> Project Selesai (Terlibat)
          </div>
          <p className="font-heading text-3xl font-bold text-slate-100">{completedProjects.length}</p>
        </Card>
        <Card className="p-5" data-testid="laporan-completed-tasks">
          <div className="mb-1 flex items-center gap-2 text-xs text-slate-400">
            <CheckCircle2 size={14} /> Total Task Selesai
          </div>
          <p className="font-heading text-3xl font-bold text-slate-100">{completedTasks.length}</p>
        </Card>
        <Card className="p-5" data-testid="laporan-yearly-performance">
          <div className="mb-1 flex items-center gap-2 text-xs text-slate-400">
            <Zap size={14} /> Performance Tahun Ini
          </div>
          <p className="font-heading text-3xl font-bold text-slate-100">
            {yearTotal > 0 ? Math.round((yearDone / yearTotal) * 100) : 0}%
          </p>
        </Card>
        <Card className="p-5" data-testid="laporan-extra-points">
          <div className="mb-1 flex items-center gap-2 text-xs text-slate-400">
            <Sparkles size={14} /> Total Poin Extra
          </div>
          <p className="font-heading text-3xl font-bold text-slate-100">{extraData.total_extra ?? 0}</p>
          <p className="mt-1 text-[10px] text-slate-500">poin di atas kapasitas</p>
        </Card>
      </div>

      {/* Performance & poin per bulan, dengan filter tahun/bulan */}
      <Card className="p-5" data-testid="laporan-monthly-chart">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="font-heading text-lg font-semibold">
              {month ? `Detail Bulan ${MONTHS.find((m) => m.value === month)?.label} — per Sprint` : `Poin per Bulan Tahun ${year}`}
            </h2>
            <p className="mt-0.5 text-xs text-slate-400">
              Performance keseluruhan periode ini:{" "}
              <span className="font-semibold text-amber-400">{overallPerformance != null ? `${overallPerformance}%` : "—"}</span>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={year} onChange={(e) => setYear(e.target.value)} data-testid="laporan-year-select">
              {buildYearOptions().map((y) => (
                <option key={y.value} value={y.value}>{y.label}</option>
              ))}
            </Select>
            <Select value={month} onChange={(e) => setMonth(e.target.value)} data-testid="laporan-month-select">
              {MONTHS.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </Select>
          </div>
        </div>

        {series.length ? (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={series}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
              <XAxis dataKey={month ? "sprint" : "label"} tick={{ fill: "#64748B", fontSize: 11 }} />
              <YAxis tick={{ fill: "#64748B", fontSize: 11 }} />
              <Tooltip contentStyle={tooltipStyle} formatter={(value, name) => (name === "Performance (%)" ? [`${value ?? 0}%`, "Performance"] : [`${value ?? 0} pts`, name])} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="output" name="Output (pts)" fill="#22C55E" radius={[4, 4, 0, 0]} maxBarSize={24} />
              <Bar dataKey="allocated" name="Allocated (pts)" fill="#3B82F6" radius={[4, 4, 0, 0]} maxBarSize={24} />
              <Bar dataKey="performance" name="Performance (%)" fill="#F59E0B" radius={[4, 4, 0, 0]} maxBarSize={24} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-sm text-slate-500">Belum ada data untuk tahun/bulan ini.</p>
        )}
      </Card>

      {/* Riwayat per Project — search global, klik project untuk lihat detail */}
      <Card className="p-5" data-testid="laporan-project-history">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-heading text-lg font-semibold">Riwayat Task per Project</h2>
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cari task di semua project..."
              className="pl-8"
              data-testid="laporan-search"
            />
          </div>
        </div>

        {searchActive ? (
          <div className="space-y-2">
            {flatSearchResults.length === 0 && (
              <p className="text-sm text-slate-500">Tidak ada task yang cocok dengan pencarian.</p>
            )}
            {flatSearchResults.map((item) => (
              <div
                key={`${item._kind}-${item.id}`}
                onClick={() => setSelectedTask(findFullTask(item))}
                className="flex cursor-pointer flex-col gap-1 rounded-lg border border-slate-800 bg-slate-950/40 p-3 transition-colors hover:border-slate-600 sm:flex-row sm:items-center sm:justify-between"
                data-testid={`laporan-search-result-${item.id}`}
              >
                <div>
                  <p className="font-medium text-slate-200">{item.title}</p>
                  <p className="text-xs text-slate-500">
                    {item.project?.name || "Internal"} · {item.weight ?? 0} pts
                  </p>
                </div>
                <span className={`text-xs ${item._kind === "extra" ? "text-amber-400" : "text-emerald-400"}`}>
                  {item._kind === "extra" ? "Extra" : "Selesai"}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {groups.length === 0 && (
              <p className="text-sm text-slate-500">Belum ada task selesai atau extra.</p>
            )}
            {groups.map((g) => {
              const isOpen = expandedKey === g.key;
              return (
                <div key={g.key} className="rounded-lg border border-slate-800 bg-slate-950/40">
                  <div
                    onClick={() => setExpandedKey(isOpen ? null : g.key)}
                    className="flex cursor-pointer items-center justify-between p-3 transition-colors hover:border-slate-600"
                    data-testid={`laporan-project-group-${g.key}`}
                  >
                    <div className="flex items-center gap-2">
                      {isOpen ? <ChevronDown size={14} className="text-slate-500" /> : <ChevronRight size={14} className="text-slate-500" />}
                      <FolderKanban size={14} className="text-slate-500" />
                      <p className="font-medium text-slate-200">{g.project?.name || "Internal"}</p>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-slate-400">
                      <span className="flex items-center gap-1 text-emerald-400"><CheckCircle2 size={12} /> {g.done.length} selesai</span>
                      {g.extra.length > 0 && (
                        <span className="flex items-center gap-1 text-amber-400"><Sparkles size={12} /> {g.extra.length} extra</span>
                      )}
                    </div>
                  </div>

                  {isOpen && (
                    <div className="space-y-3 border-t border-slate-800 p-3">
                      {g.done.length > 0 && (
                        <div>
                          <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Task Selesai</p>
                          <div className="space-y-1.5">
                            {g.done.map((t) => (
                              <div
                                key={t.id}
                                onClick={() => setSelectedTask(t)}
                                className="flex cursor-pointer flex-col gap-1 rounded-md border border-slate-800 bg-slate-900/60 p-2.5 transition-colors hover:border-slate-600 sm:flex-row sm:items-center sm:justify-between"
                                data-testid={`laporan-done-task-${t.id}`}
                              >
                                <p className="text-sm text-slate-200">{t.title}</p>
                                <div className="flex items-center gap-3 text-xs text-slate-400">
                                  <span>{t.weight ?? 0} pts</span>
                                  {t.deadline && (
                                    <span className="flex items-center gap-1">
                                      <Calendar size={12} /> {new Date(t.deadline).toLocaleDateString("id-ID")}
                                    </span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {g.extra.length > 0 && (
                        <div>
                          <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Task Extra</p>
                          <div className="space-y-1.5">
                            {g.extra.map((item) => (
                              <div
                                key={item.id}
                                onClick={() => setSelectedTask(findFullTask(item))}
                                className="flex cursor-pointer flex-col gap-1 rounded-md border border-slate-800 bg-slate-900/60 p-2.5 transition-colors hover:border-slate-600 sm:flex-row sm:items-center sm:justify-between"
                                data-testid={`laporan-extra-task-${item.id}`}
                              >
                                <p className="text-sm text-slate-200">{item.title}</p>
                                <div className="flex items-center gap-3 text-xs text-amber-400">
                                  <span>{item.weight ?? 0} pts</span>
                                  <span>{item.sprint}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <TaskModal
        open={!!selectedTask}
        onClose={() => setSelectedTask(null)}
        task={selectedTask}
        mode="view"
        onSaved={() => {}}
      />
    </div>
  );
}

function Metric({ icon: Icon, color, label, value, unit }) {
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-950/50 p-3">
      <div className="mb-1 flex items-center gap-1.5 text-xs text-slate-400">
        <Icon size={13} style={{ color }} /> {label}
      </div>
      <p className="font-mono text-2xl font-bold tabular-nums" style={{ color }}>
        {value} <span className="text-sm text-slate-500">{unit}</span>
      </p>
    </div>
  );
}
